import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  NOTICE_EFFECTIVE_DATE_HELP,
  NoticeEffectiveDateHelpVariant,
} from '../../../constants/notice-effective-date-help';

export function todayDateInputValue(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function dateInputToIsoStartOfDay(dateStr: string): string {
  return `${dateStr}T00:00:00.000Z`;
}

export type EffectiveDateSectionProps = {
  noticeHelpVariant: NoticeEffectiveDateHelpVariant;
  useCustomEffectiveDate: boolean;
  onUseCustomEffectiveDateChange: (value: boolean) => void;
  effectiveDate: string;
  onEffectiveDateChange: (value: string) => void;
  showIncludeOtp?: boolean;
  includeOtp?: boolean;
  onIncludeOtpChange?: (value: boolean) => void;
  deliveryIncludesSms?: boolean;
};

const EffectiveDateSection: React.FC<EffectiveDateSectionProps> = ({
  noticeHelpVariant,
  useCustomEffectiveDate,
  onUseCustomEffectiveDateChange,
  effectiveDate,
  onEffectiveDateChange,
  showIncludeOtp = false,
  includeOtp = false,
  onIncludeOtpChange,
  deliveryIncludesSms = false,
}) => {
  const today = useMemo(() => todayDateInputValue(), []);
  const includeOtpEnabled = useMemo(() => {
    if (!showIncludeOtp || !deliveryIncludesSms) return false;
    if (!useCustomEffectiveDate) return true;
    return effectiveDate === today;
  }, [showIncludeOtp, deliveryIncludesSms, useCustomEffectiveDate, effectiveDate, today]);

  const effectiveHelp = NOTICE_EFFECTIVE_DATE_HELP[noticeHelpVariant];

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={useCustomEffectiveDate}
              onChange={(e) => {
                const checked = e.target.checked;
                onUseCustomEffectiveDateChange(checked);
                if (!checked) {
                  onEffectiveDateChange(today);
                }
              }}
            />
          }
          label="Set a custom effective date (applies from)"
        />
        <Tooltip title={effectiveHelp} placement="top" enterTouchDelay={0}>
          <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
        </Tooltip>
      </Box>

      {!useCustomEffectiveDate && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
          Notice applies from the date it is sent (today). Assigned date is recorded automatically.
        </Typography>
      )}

      {!useCustomEffectiveDate && showIncludeOtp && deliveryIncludesSms && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeOtp}
                onChange={(e) => onIncludeOtpChange?.(e.target.checked)}
              />
            }
            label="Include OTP in SMS"
          />
          <Tooltip title={NOTICE_EFFECTIVE_DATE_HELP.CONSENT_NOTICE_OTP} placement="top" enterTouchDelay={0}>
            <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
          </Tooltip>
        </Box>
      )}

      {useCustomEffectiveDate && (
        <Box sx={{ mt: 1.5, ml: 4, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label="Effective date (applies from)"
            type="date"
            size="small"
            value={effectiveDate}
            onChange={(e) => {
              const v = e.target.value;
              onEffectiveDateChange(v);
              if (v !== today) {
                onIncludeOtpChange?.(false);
              }
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
            fullWidth
          />

          {showIncludeOtp && deliveryIncludesSms && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeOtp && includeOtpEnabled}
                    disabled={!includeOtpEnabled}
                    onChange={(e) => onIncludeOtpChange?.(e.target.checked)}
                  />
                }
                label="Include OTP in SMS"
              />
              <Tooltip
                title={
                  includeOtpEnabled
                    ? NOTICE_EFFECTIVE_DATE_HELP.CONSENT_NOTICE_OTP
                    : NOTICE_EFFECTIVE_DATE_HELP.CONSENT_NOTICE_LINK
                }
                placement="top"
                enterTouchDelay={0}
              >
                <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
              </Tooltip>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EffectiveDateSection;
